/**
 * Divergence band: the contiguous stretch of a zone bracketing the PEAK of
 * per-bin lap-speed divergence (worst |lap − bin median| per 5 m bin).
 * Selection is RELATIVE — every zone has a relatively-worst region, exactly
 * like the map variance gradient always has a reddest spot — so a band is
 * emitted even when all laps are tight. Null only means the data can't
 * support the statistic. Speed is the basis even on the brake-% chart.
 * The map gradient (consistencyGradient.ts) consumes the SAME series via
 * binSpeedSpreads.ts, so the two surfaces agree by construction.
 */
import {
  SAMPLE_INTERVAL,
  MIN_BAND_BINS,
  BAND_EXTENT_RATIO,
  MAX_BAND_FRACTION,
} from '../../../config/chartDisplay'
import { type ConsistencyDisplayBounds } from './consistencyThresholds'
import { computeZoneSpreadContext } from './binSpeedSpreads'
import { type SpeedTrace } from './speedTraces'

/**
 * One-line caption rendered under the zone chart on BOTH clients. States the
 * band's basis: it always measures SPEED spread, whatever the chart plots.
 */
export const CONSISTENCY_BAND_CAPTION =
  'Dashed band: where lap speeds spread most in this zone'

export interface ConsistencyBand {
  /** Absolute course distances, same frame as PercentMarker.absoluteDistance. */
  startDistance: number
  endDistance: number
}

export interface BuildConsistencyBandArgs {
  /** Per-lap speed traces on the frame the chart plots (pass 29) — clients
   *  adapt their already-recalibrated chart data via speedTraces.ts. */
  traces: SpeedTrace[]
  excludedLapIds: Set<string>
  displayBounds: ConsistencyDisplayBounds
}

export function buildConsistencyBand({
  traces,
  excludedLapIds,
  displayBounds,
}: BuildConsistencyBandArgs): ConsistencyBand | null {
  const ctx = computeZoneSpreadContext(traces, excludedLapIds, displayBounds)
  if (ctx === null) return null
  // Per-bin worst deviation; null entries = insufficient data (break extension).
  const { startDist, numBins, spreads } = ctx

  // Peak bin: highest spread, first on ties → deterministic.
  let peakBin = -1
  let peakSpread = -Infinity
  for (let bin = 0; bin < numBins; bin++) {
    const s = spreads[bin]
    if (s !== null && s > peakSpread) {
      peakSpread = s
      peakBin = bin
    }
  }
  if (peakBin === -1) return null

  // Extend from the peak while bins are valid and stay near the peak.
  const qualifies = (bin: number): boolean => {
    const s = spreads[bin]
    return s !== null && s >= BAND_EXTENT_RATIO * peakSpread
  }
  let first = peakBin
  while (first > 0 && qualifies(first - 1)) first--
  let last = peakBin
  while (last < numBins - 1 && qualifies(last + 1)) last++

  // Cap the width, trimming the end farthest from the peak first.
  const maxBins = Math.max(
    MIN_BAND_BINS,
    Math.floor(numBins * MAX_BAND_FRACTION)
  )
  while (last - first + 1 > maxBins) {
    if (peakBin - first > last - peakBin) first++
    else last--
  }

  // Floor: always ≥ MIN_BAND_BINS, widening around the peak.
  while (last - first + 1 < MIN_BAND_BINS) {
    if (first > 0) first--
    else if (last < numBins - 1) last++
    else break
  }

  return {
    startDistance: startDist + first * SAMPLE_INTERVAL,
    endDistance: Math.min(
      startDist + (last + 1) * SAMPLE_INTERVAL,
      displayBounds.endDistance
    ),
  }
}
